import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppDateField } from '../../components/AppDateField';
import { AppScreen } from '../../components/AppScreen';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { normalizeApiError } from '../../api/apiError';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  assignEmployeeShift,
  getEmployeeCurrentShift,
} from '../../services/employeeService';
import { getShifts } from '../../services/shiftService';
import { activeConfigurationFilters } from '../../utils/configurationFilters';
import { formatPermissionTime, toCalendarDateString } from '../../utils/dateTime';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'EmployeeAssignShift'>;

function shiftOptionSubtitle(
  code: string | null | undefined,
  start: string | null | undefined,
  end: string | null | undefined,
) {
  if (start && end) {
    const range = `${formatPermissionTime(start)} – ${formatPermissionTime(end)}`;
    return code ? `${code} · ${range}` : range;
  }
  return code ?? null;
}

export function EmployeeAssignShiftScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const employeeId = route.params.employeeId;
  const [shift, setShift] = useState<SelectorOption | null>(null);
  const [currentHours, setCurrentHours] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState(
    toCalendarDateString(new Date()),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const hasCurrent = Boolean(currentHours || shift);
  const screenTitle = hasCurrent ? 'Update shift' : 'Assign shift';

  const themed = useMemo(
    () => ({
      error: { ...typography.body, color: palette.error },
      hint: { ...typography.caption, color: palette.textMuted },
    }),
    [palette],
  );

  useEffect(() => {
    let cancelled = false;
    getEmployeeCurrentShift(employeeId)
      .then(current => {
        if (cancelled || !current) return;
        setCurrentHours(current.shiftHours);
        if (current.shiftId) {
          setShift({
            id: current.shiftId,
            label: current.shiftName ?? `Shift ${current.shiftHours ?? current.shiftId}`,
            subtitle: shiftOptionSubtitle(
              current.shiftCode,
              current.shiftStartTime,
              current.shiftEndTime,
            ),
          });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  const loadShifts = useCallback(async (search: string): Promise<SelectorOption[]> => {
    const result = await getShifts({
      page: 1,
      pageSize: 20,
      filters: activeConfigurationFilters({ search: search || undefined }),
    });
    return result.items.map(item => ({
      id: item.shiftId,
      label: item.shiftName,
      subtitle: shiftOptionSubtitle(
        item.shiftCode,
        item.shiftStartTime,
        item.shiftEndTime,
      ),
    }));
  }, []);

  async function submit() {
    if (busy) return;
    setError('');
    if (!shift) {
      setError('Select a shift to assign.');
      return;
    }
    const date = effectiveFrom.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setError('Effective from must use YYYY-MM-DD format.');
      return;
    }

    setBusy(true);
    try {
      await runOperation(hasCurrent ? 'update' : 'add', () => assignEmployeeShift(employeeId, {
        shiftId: shift.id,
        effectiveFrom: date,
      }));
      navigation.goBack();
    } catch (caught) {
      setError(
        normalizeApiError(caught).message || 'Unable to update shift.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.EMPLOYEE_UPDATE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll keyboardSafe>
        <ScreenHeader title={screenTitle} onBack={navigation.goBack} />
        <View style={styles.body}>
          <AppCard style={styles.card}>
            <Text style={themed.hint}>
              Choose a shift to change this employee's hours. The new time
              applies from the effective date.
            </Text>
            {currentHours ? (
              <Text
                accessibilityLabel={`Current shift time: ${currentHours}`}
                style={themed.hint}
              >
                Current shift time: {currentHours}
              </Text>
            ) : null}
            <SearchableEntitySelector
              label="Shift *"
              selectedValue={shift?.id ?? null}
              selectedLabel={shift?.label ?? null}
              loadOptions={loadShifts}
              onSelect={setShift}
              onClear={() => setShift(null)}
              placeholder="Search shifts"
            />
            <AppDateField
              label="Effective from *"
              value={effectiveFrom}
              onChange={setEffectiveFrom}
            />
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <AppButton
              title={
                busy
                  ? hasCurrent
                    ? 'Updating…'
                    : 'Assigning…'
                  : hasCurrent
                    ? 'Update shift'
                    : 'Assign shift'
              }
              accessibilityLabel="Submit assign shift"
              onPress={submit}
              disabled={busy}
            />
            <AppButton
              title="Cancel"
              variant="secondary"
              onPress={() => navigation.goBack()}
              disabled={busy}
            />
          </AppCard>
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  card: { gap: spacing.md },
});
